import React from 'react';
import { Box, Text, UnstyledButton, rem } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { COLLAPSE_BRANCH, DURATION, EASE_OUT, SPRING } from '@/theme/motion';
import { TOUCH_TARGET, useIsTouch } from '@/hooks/use.responsive';
import { navigationContains } from '@/function/navigation/navigation.find';
import type { TypeNavNode } from '@/types/ui/types.ui.navigation';

type Props = {
    node: TypeNavNode;
    depth: number;
    pathname: string;
    openBranches: string[];
    onToggleBranch: (id: string) => void;
    onNavigate: (node: TypeNavNode) => void;
    badges?: Record<string, number>;
};

const INDENT = 22;

const ShellNavbarNode: React.FC<Props> = ({ node, depth, pathname, openBranches, onToggleBranch, onNavigate, badges }) => {
    const isTouch = useIsTouch();
    const reduced = useReducedMotion();
    const hasChildren = Boolean(node.children?.length);
    const isOpen = hasChildren && openBranches.includes(node.id);
    const isCurrent = !node.expandOnly
        && (pathname === node.path || (pathname.startsWith(`${node.path}/`) && !node.children?.some((child) => navigationContains(child, pathname))));
    const inTrail = !isCurrent && navigationContains(node, pathname);
    const Icon = node.icon;

    const rowColor = isCurrent ? 'var(--bd-brand-text)' : inTrail ? 'var(--bd-text-strong)' : 'var(--mantine-color-text)';

    return (
        <Box>
            <UnstyledButton
                onClick={() => {
                    if (node.expandOnly) { onToggleBranch(node.id); return; }
                    onNavigate(node);
                    if (hasChildren && !isOpen) onToggleBranch(node.id);
                }}
                aria-current={isCurrent ? 'page' : undefined}
                aria-expanded={hasChildren ? isOpen : undefined}
                data-active={isCurrent || undefined}
                style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    height: rem(isTouch ? TOUCH_TARGET : depth === 0 ? 38 : 34),
                    paddingLeft: rem(12 + depth * INDENT),
                    paddingRight: rem(6),
                    gap: rem(10),
                    borderRadius: 'var(--mantine-radius-md)',
                    color: rowColor,
                    transition: 'color 140ms ease, background-color 140ms ease',
                }}
                onMouseEnter={(event) => { if (!isCurrent) event.currentTarget.style.backgroundColor = 'var(--bd-surface-hover)'; }}
                onMouseLeave={(event) => { event.currentTarget.style.backgroundColor = ''; }}
            >
                {isCurrent ? (
                    <motion.span
                        layoutId="navbar-active-item"
                        transition={reduced ? { duration: 0 } : SPRING}
                        aria-hidden
                        style={{ position: 'absolute', inset: 0, borderRadius: 'var(--mantine-radius-md)', backgroundColor: 'var(--bd-brand-soft)' }}
                    />
                ) : null}
                {isCurrent ? (
                    <motion.span
                        layoutId="navbar-active-bar"
                        transition={reduced ? { duration: 0 } : SPRING}
                        aria-hidden
                        style={{ position: 'absolute', left: 0, top: rem(8), bottom: rem(8), width: rem(3), borderRadius: rem(3), backgroundColor: 'var(--bd-brand-text)' }}
                    />
                ) : null}

                <Icon
                    style={{ position: 'relative', width: rem(depth === 0 ? 20 : 16), height: rem(depth === 0 ? 20 : 16), flexShrink: 0 }}
                    stroke={1.7}
                />
                <Text
                    size="sm"
                    fw={isCurrent ? 650 : inTrail ? 600 : 500}
                    style={{ position: 'relative', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                    {node.label}
                </Text>

                {badges?.[node.id] && !(hasChildren && isOpen) ? (
                    <span className="bd-nav-badge" aria-label={`${badges[node.id]} da leggere`}>{badges[node.id] > 99 ? '99+' : badges[node.id]}</span>
                ) : null}

                {hasChildren ? (
                    <Box
                        component="span"
                        role="button"
                        tabIndex={0}
                        aria-label={isOpen ? `Chiudi ${node.label}` : `Apri ${node.label}`}
                        onClick={(event) => { event.stopPropagation(); onToggleBranch(node.id); }}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.stopPropagation(); onToggleBranch(node.id); }
                        }}
                        style={{
                            position: 'relative', display: 'grid', placeItems: 'center',
                            width: rem(26), height: rem(26), borderRadius: rem(6), flexShrink: 0,
                            color: 'var(--mantine-color-dimmed)',
                        }}
                    >
                        <motion.span
                            animate={{ rotate: isOpen ? 90 : 0 }}
                            transition={{ duration: DURATION.fast, ease: EASE_OUT }}
                            style={{ display: 'flex' }}
                        >
                            <IconChevronRight size={14} stroke={2} />
                        </motion.span>
                    </Box>
                ) : null}
            </UnstyledButton>

            <AnimatePresence initial={false}>
                {isOpen && node.children ? (
                    <motion.div
                        key="children"
                        variants={COLLAPSE_BRANCH}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        style={{ overflow: 'hidden' }}
                    >
                        <Box
                            style={{
                                position: 'relative',
                                marginTop: rem(2),
                                display: 'flex', flexDirection: 'column', gap: rem(2),
                            }}
                        >
                            <span
                                aria-hidden
                                style={{
                                    position: 'absolute', top: rem(4), bottom: rem(4),
                                    left: rem(12 + depth * INDENT + 9),
                                    width: 1, backgroundColor: 'var(--bd-border)',
                                }}
                            />
                            {node.children.map((child) => (
                                <ShellNavbarNode
                                    key={child.id}
                                    node={child}
                                    depth={depth + 1}
                                    pathname={pathname}
                                    openBranches={openBranches}
                                    onToggleBranch={onToggleBranch}
                                    onNavigate={onNavigate}
                                    badges={badges}
                                />
                            ))}
                        </Box>
                    </motion.div>
                ) : null}
            </AnimatePresence>
        </Box>
    );
};

export default ShellNavbarNode;
